/**
 * Login Component
 *
 * This component handles the user authentication process, including login and password reset functionalities.
 * It validates user input, performs an API request to log the user in, and redirects the user to the dashboard or change password page.
 *
 * ### Features:
 * 1. **Login Functionality**:
 *    - Validates email and password inputs using utility functions.
 *    - Makes an API request to authenticate the user.
 *    - Updates the authentication context (`AuthContext`) with user data and token on successful login.
 *    - Redirects the user to the dashboard or change password page based on the API response.
 *
 * 2. **Forgot Password Functionality**:
 *    - Allows users to request a password reset by providing their email.
 *    - Validates the reset email and sends a password reset request via the API.
 *    - Displays a success message or error based on the API response.
 *
 * 3. **Error Handling**:
 *    - Displays validation errors for email and password fields.
 *    - Handles API errors and displays appropriate error messages to the user.
 *
 * 4. **Loading State**:
 *    - Manages a loading state during API requests to provide feedback to the user.
 *
 * 5. **UI State Management**:
 *    - Toggles between login and forgot password views.
 *    - Manages error messages and loading states dynamically.
 *
 * ### Dependencies:
 * - **React**: For building the component and managing state.
 * - **React Router**: For navigation and redirection (`useNavigate`).
 * - **LoginLogic Component**: For rendering the login UI and handling user interactions.
 * - **Validation Utilities**: `validateEmail` and `validatePassword` for input validation.
 * - **Dependency Injection Container**: Resolves services like `franchiseService` and `Logger`.
 * - **franchiseService**: Handles API requests for login and password reset.
 * - **Logger**: Logs errors and other important events.
 * - **AuthContext**: Manages global authentication state and provides the `login` function.
 *
 * ### Workflow:
 * 1. The user enters their email and password.
 * 2. The component validates the inputs and displays errors if any.
 * 3. On successful validation, an API request is made to log the user in.
 * 4. If the login is successful:
 *    - The user data and token are stored in the `AuthContext`.
 *    - The user is redirected to the dashboard or change password page (if required).
 * 5. If the login fails, an error message is displayed.
 * 6. The user can toggle to the "Forgot Password" view to request a password reset.
 * 7. On successful password reset request, a confirmation message is displayed.
 *
 * ### Error Handling:
 * - Validation errors (e.g., invalid email or password) are displayed immediately.
 * - API errors (e.g., invalid credentials, server issues) are logged and displayed to the user.
 *
 * ### Usage:
 * This component is typically used as the entry point for user authentication in the application.
 * It should be placed in a route (e.g., `/login`) and integrated with the application's routing system.
 */
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import LoginLogic from "../../presentation/component/LoginLogic"
import {
  validateEmail,
  validatePassword,
} from "../../infrastructure/utils/validation"
import { container } from "../../infrastructure/container"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import { LoginError } from "../../infrastructure/models/login"
import { Logger } from "../../infrastructure/services/logger"
import { useAuth } from "../context/AuthContext"

// Interfaces for managing form state
interface FormState {
  email: string
  password: string
  resetEmail: string
}

interface UiState {
  error: string
  loading: boolean
  forgotPassword: boolean
}

const Login: React.FC = () => {
  // Resolve services using dependency injection
  const franchiseService = container.resolve<FranchiseService>("franchiseService")
  const logger = container.resolve<Logger>("logger")

  // Use React Router's `useNavigate` hook to handle redirection
  const navigate = useNavigate()

  // Use `useAuth` hook to get authentication state and functions
  const { login } = useAuth() // `login` is the function from AuthContext to set authentication state

  // Define form and UI states
  const [formState, setFormState] = useState<FormState>({
    email: "",
    password: "",
    resetEmail: "",
  })

  const [uiState, setUiState] = useState<UiState>({
    error: "",
    loading: false,
    forgotPassword: false,
  })

  const { email, password, resetEmail } = formState
  const { error, loading, forgotPassword } = uiState

  /**
   * Handles changes to form inputs (email, password, reset email).
   * This function updates the form state for the respective input field.
   *
   * @param field - The field that is being updated (email, password, etc.)
   * @param value - The new value of the input field
   */
  const handleInputChange = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
  }

  /**
   * Updates the UI state (error, loading, forgot password visibility).
   * This function is used to control the UI state during login and password reset operations.
   *
   * @param field - The field being updated (error, loading, etc.)
   * @param value - The new value of the field
   */
  const updateUiState = (field: keyof UiState, value: string | boolean) => {
    setUiState((prev) => ({ ...prev, [field]: value }))
  }

  /**
   * Set loading state to true and clear any previous errors
   */
  const showLoading = () => {
    updateUiState("loading", true)
    updateUiState("error", "")
  }

  /**
   * Handles the login process by validating the form, making an API request, and handling the response.
   * If login is successful, it updates the auth context and redirects the user accordingly.
   */
  const handleLogin = async () => {
    showLoading()

    // Validate the email and password inputs
    const emailError = validateEmail(email)
    const passwordError = validatePassword(password)

    // If validation fails, update the UI with error and stop loading
    if (emailError || passwordError) {
      updateUiState("error", emailError || passwordError)
      updateUiState("loading", false)
      return
    }

    try {
      // Attempt to log the user in by calling the login API
      const response = await franchiseService.login(email, password)
      console.log("api response", response)
      // Validate the response object
      if (!response) {
        throw new Error("Invalid response from server.")
      }

      const code = response?.code
      // Handle the response data (e.g., password change requirement)
      if (code === "P01" || code === "PW01") {
        alert("You must change your password.")
        navigate("/change-password", { replace: true })
        return
      }

      // Ensure the response contains user data before calling `login`
      if (!response.user) {
        throw new Error("User data not found in response.")
      }

      login(response)

      navigate("/dashboard", { replace: true })
    } catch (err) {
      // Log any errors that occur during login
      if (err as LoginError) {
        logger.error("🚨 Error during login:", err.error)
      }
      logger.error("🚨 Error during login:", err)
      // Update the UI with the error message
      updateUiState("error", err.message || "Login failed.")
    } finally {
      // Reset loading state after login attempt
      updateUiState("loading", false)
    }
  }

  /**
   * Handles the forgot password process by validating the reset email and making an API request.
   */
  const handleForgotPassword = async () => {
    showLoading()

    // Validate the reset email
    const emailError = validateEmail(resetEmail)

    if (emailError) {
      updateUiState("error", emailError)
      updateUiState("loading", false)
      return
    }

    try {
      await franchiseService.forgotPassword(resetEmail)
      alert("Password reset email sent.")
      updateUiState("forgotPassword", false)
    } catch (err: any) {
      const errorMessage =
        (err.code ? `[${err.code}] ` : "") + (err.message || "Failed to send reset email.")
      updateUiState("error", errorMessage)
    } finally {
      updateUiState("loading", false)
    }
  }

  return (
    <LoginLogic
      email={email}
      setemail={(value: string) => handleInputChange("email", value)}
      password={password}
      setPassword={(value: string) => handleInputChange("password", value)}
      error={error}
      loading={loading}
      handleLogin={handleLogin}
      forgotPassword={forgotPassword}
      setForgotPassword={(value: boolean) =>
        updateUiState("forgotPassword", value)
      }
      resetEmail={resetEmail}
      setResetEmail={(value: string) => handleInputChange("resetEmail", value)}
      handleForgotPassword={handleForgotPassword}
    />
  )
}

export default Login